import {
  useAppDispatch,
  useAppSelector,
} from "../../../../hooks/useTypedSelector";
import { useTranslation } from "react-i18next";
import {
  TableFiltersWrapper,
  TableFilters,
  TableFilterInputWrapper,
} from "../../../general/table/modules/commons/tableFilters/tableFilters";
import Select from "react-select";
import { Form } from "react-bootstrap";
import { useEffect } from "react";
import { invoiceStatusList } from "../../domain/lists/invoiceStatusList";
import { EntitySelectField } from "../../../general/fields/modules/commons/entitySelectField/entitySelectField";
import { relationSchema } from "../../../relation/domain/models/RelationModel";
import {
  setInvoiceFilters,
  resetInvoiceFilters,
} from "../../infrastructure/redux/InvoiceDataSlice";
import { useSearchParams } from "react-router-dom";

type InvoiceFilters = {
  relationId?: string;
  status: string;
  InStorageFrom: string;
  InStorageUntil: string;
};

export const InvoiceTableFilters = () => {
  const { t } = useTranslation("common");
  const dispatch = useAppDispatch();
  const filters = useAppSelector(
    (state: { invoiceData: { filters: InvoiceFilters } }) =>
      state.invoiceData.filters,
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const statusOptions = invoiceStatusList.map((status) => ({
    value: status,
    label: t(`invoice.status.${status}`),
  }));

  useEffect(() => {
    const urlRelationId = searchParams.get("relationId");
    const urlStatus = searchParams.get("status");
    const urlInStorageFrom = searchParams.get("InStorageFrom");
    const urlInStorageUntil = searchParams.get("InStorageUntil");
    let statusToSet = urlStatus || filters.status;
    if (!statusToSet) {
      statusToSet = "on_invoice";
      searchParams.set("status", statusToSet);
      setSearchParams(searchParams);
    }
    dispatch(
      setInvoiceFilters({
        relationId: urlRelationId || "",
        status: statusToSet,
        InStorageFrom: urlInStorageFrom || "",
        InStorageUntil: urlInStorageUntil || "",
      }),
    );
  }, []);

  const updateFilter = (key: keyof InvoiceFilters, value: string) => {
    dispatch(setInvoiceFilters({ [key]: value }));
    const params = new URLSearchParams(searchParams.toString());
    params.set(key as string, value || "");
    setSearchParams(params);
  };
  const removeFilter = (key: keyof InvoiceFilters) => {
    dispatch(setInvoiceFilters({ [key]: "" }));
    const params = new URLSearchParams(searchParams.toString());
    params.delete(key as string);
    setSearchParams(params);
  };

  const handleReset = () => {
    dispatch(resetInvoiceFilters());
    setSearchParams("");
  };

  return (
    <TableFiltersWrapper>
      <TableFilters>
        <TableFilterInputWrapper md={3}>
          <label>{t("Dealer")}</label>
          <EntitySelectField
            entity={"Relation"}
            prefix={"relations"}
            onChange={(option: unknown) => {
              if (option == null) {
                removeFilter("relationId");
                return;
              }
              const { value } = option as { value: string };
              updateFilter("relationId", value);
            }}
            limit={100}
            clearable={true}
            schema={relationSchema}
            value={filters.relationId}
            key={filters.relationId || "empty"}
          />
        </TableFilterInputWrapper>
        <TableFilterInputWrapper md={3}>
          <label>{t("Status")}</label>
          <Select
            options={statusOptions}
            value={
              statusOptions.find((o) => o.value === filters.status) || null
            }
            onChange={(option: unknown) => {
              if (option && typeof option === "object" && "value" in option)
                updateFilter("status", (option as { value: string }).value);
              else removeFilter("status");
            }}
            isClearable
            placeholder={t("Select...")}
            classNamePrefix="entity-select"
          />
        </TableFilterInputWrapper>
        <TableFilterInputWrapper md={3}>
          <label>{t("In/out storage from")}</label>
          <Form.Control
            type="date"
            value={filters.InStorageFrom || ""}
            onChange={(e) => {
              if (e.target.value) updateFilter("InStorageFrom", e.target.value);
              else removeFilter("InStorageFrom");
            }}
          />
        </TableFilterInputWrapper>
        <TableFilterInputWrapper md={3}>
          <label>{t("In/out storage until")}</label>
          <Form.Control
            type="date"
            value={filters.InStorageUntil || ""}
            onChange={(e) => {
              if (e.target.value)
                updateFilter("InStorageUntil", e.target.value);
              else removeFilter("InStorageUntil");
            }}
          />
        </TableFilterInputWrapper>
      </TableFilters>
      <div className="d-flex justify-content-end mt-2">
        <button
          type="button"
          className="btn btn-light btn-sm reset-filters"
          onClick={handleReset}
        >
          {t("Reset filters")}
        </button>
      </div>
    </TableFiltersWrapper>
  );
};
